import { ReactNode } from 'react';
import { type Lang, DEFAULT_LANG, getUi } from '../../functions/lib/i18n';

interface LayoutProps {
  children: ReactNode;
  theme?: 'dark' | 'light';
  onToggleTheme?: () => void;
  lang?: Lang;
  languages?: { code: string; native: string }[];
  onLangChange?: (code: string) => void;
}

/**
 * Main layout component with header and content area
 */
export function Layout({ children, theme = 'dark', onToggleTheme, lang = DEFAULT_LANG, languages, onLangChange }: LayoutProps) {
  const ui = getUi(lang);
  return (
    <div className="layout">
      <header className="header">
        <div className="header-content">
          <div className="header-brand">
            <a href="/" className="logo">
              <svg className="logo-icon" viewBox="0 0 100 100" width="32" height="32" aria-hidden="true">
                <defs>
                  <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{stopColor: '#ff4444'}} />
                    <stop offset="100%" style={{stopColor: '#cc0000'}} />
                  </linearGradient>
                </defs>
                <polygon points="10,5 10,95 95,50" fill="url(#logoGrad)" />
              </svg>
              <span className="logo-text">YouTubeMost</span>
            </a>
            <p className="tagline">{ui.tagline}</p>
          </div>
          <div className="header-actions">
            {languages && onLangChange && (
              <select
                className="lang-switcher"
                aria-label={ui.languageAriaLabel}
                value={lang}
                onChange={(e) => onLangChange(e.target.value)}
              >
                {languages.map((l) => (
                  <option key={l.code} value={l.code}>{l.native}</option>
                ))}
              </select>
            )}
            {onToggleTheme && (
              <button
                className="theme-toggle-header"
                onClick={onToggleTheme}
                aria-label={theme === 'dark' ? ui.themeToLight : ui.themeToDark}
                title={theme === 'dark' ? ui.themeToLight : ui.themeToDark}
              >
                {theme === 'dark' ? '☀️' : '🌙'}
              </button>
            )}
          </div>
        </div>
      </header>
      <main className="main-content">
        {children}
      </main>
      <footer className="footer">
        <p className="footer-about">{ui.footerAbout}</p>
        <p className="footer-copyright">
          YouTubeMost &copy; {new Date().getFullYear()} ·
          {' '}{ui.footerPoweredBy}{' '}
          <a href="https://developers.google.com/youtube/v3" target="_blank" rel="noopener noreferrer">YouTube Data API</a> ·
          {' '}{ui.footerBuiltBy}{' '}
          <a href="https://roblesconsultingllc.com" target="_blank" rel="noopener noreferrer">Robles Consulting LLC</a>
        </p>
      </footer>
    </div>
  );
}
